import { useEffect, useRef, useState } from 'react';

import { isEmpty, isNonNullish } from 'remeda';
import { toast } from 'sonner';
import { BaseButton, ConfirmationModal, SettingsLayout } from 'wbs-components-lib';

import { ServerDataGridPublicMethods } from '@shared/components';
import { ReferralRewardUserProgressModel, RewardModelWithChance } from '@shared/types';
import { getChangedRewardsInfo, transformRewardsFromMapToObject, transformRewardsFromObjectToMap } from '@shared/utils';

import {
  useBulkDeleteRewardsMutation,
  useCreateRewardMutation,
  useGetReferralRewardSettingsQuery,
  useLazyExportReferralSystemUsersProgressQuery,
  useLazyGetReferralUserRewardProgressQuery,
  useLazyGetRewardsByIdsQuery,
  useMarkReferralRewardAsReceivedMutation,
  useUpdateReferralRewardSettingsMutation,
  useUpdateRewardMutation,
} from '@services';

import { REFERRAL_SETTINGS_DEFAULT_VALUES } from '../constants';
import { ReferralAssignModal } from '../ReferralAssignModal';
import { ReferralRewardSettingsForm, ReferralRewardSettingsFormValues } from '../ReferralRewardSettingsForm';
import { ReferralRewardUserUpdateModal } from '../ReferralRewardUserUpdateModal';
import { ReferralUsersList } from '../ReferralUsersList';

import { getReferralSystemEditFormInitialValues } from './utils';

import styles from './ReferralSystemSettings.module.css';

import { PageLayout } from 'v2/components/pageLayout/PageLayout';

const { exportButton } = styles;

export const ReferralSystemSettings = () => {
  const serverDataGridRef = useRef<ServerDataGridPublicMethods>(null);

  const [initialRewards, setInitialRewards] = useState<RewardModelWithChance[]>([]);
  const [currentUserId, setCurrentUserId] = useState('');
  const [isUserChanceEditModalOpened, setIsUserChanceEditModalOpened] = useState(false);
  const [isAssignUserModalOpened, setIsAssignUserModalOpened] = useState(false);
  const [currentUserRewardChance, setCurrentUserRewardChance] = useState<Record<string, number>>({});
  const [currentUserProgress, setCurrentUserProgress] = useState<ReferralRewardUserProgressModel | null>(null);
  const [markReferralRewardModalOpened, setMarkReferralRewardModalOpened] = useState(false);

  const { data: referralRewardSettings } = useGetReferralRewardSettingsQuery();
  const [getReferralUserRewardProgress] = useLazyGetReferralUserRewardProgressQuery();
  const [exportReferralSystemUsersProgress] = useLazyExportReferralSystemUsersProgressQuery();

  const [updateReferralRewardSettings, { isLoading: isReferralsSettingsUpdating }] =
    useUpdateReferralRewardSettingsMutation();
  const [markReferralRewardAsReceived] = useMarkReferralRewardAsReceivedMutation();

  const [getRewardsByIds] = useLazyGetRewardsByIdsQuery();
  const [bulkDeleteRewards] = useBulkDeleteRewardsMutation();
  const [createReward] = useCreateRewardMutation();
  const [updateReward] = useUpdateRewardMutation();

  useEffect(() => {
    if (referralRewardSettings === undefined) {
      return;
    }

    const { rewardsChances } = referralRewardSettings;

    const getInitialRewards = async () => {
      const currentRewards = await getRewardsByIds(Object.keys(rewardsChances)).unwrap();

      setInitialRewards(transformRewardsFromObjectToMap(rewardsChances, currentRewards));
    };

    getInitialRewards();
  }, [referralRewardSettings]);

  const refreshReferralsTable = async () => {
    await serverDataGridRef.current?.refreshList();
  };

  const handleExportUsersProgress = async () => {
    await exportReferralSystemUsersProgress().unwrap();
  };

  const handleUpdatingReferralsSettings = async (formValues: ReferralRewardSettingsFormValues) => {
    const { rewardsChances, startDate, ...settingsData } = formValues;
    const { deletedRewardIds, createdRewardItems, updatedRewardItems, restRewardItems } = getChangedRewardsInfo(
      initialRewards,
      rewardsChances
    );

    const isDeletedRewardsExisting = deletedRewardIds.length !== 0;

    if (isDeletedRewardsExisting) {
      await bulkDeleteRewards(deletedRewardIds);
    }

    let createdRewards: RewardModelWithChance[] = [];
    let updatedRewards: RewardModelWithChance[] = [];

    const isCreatedRewardsExisting = createdRewardItems.length !== 0;

    if (isCreatedRewardsExisting) {
      createdRewards = await Promise.all(
        createdRewardItems.map(async (reward) => {
          const { id, type, value } = await createReward(reward).unwrap();

          return new RewardModelWithChance(id, type, value, reward.chance);
        })
      );
    }

    const isUpdatedRewardsExisting = updatedRewardItems.length !== 0;

    if (isUpdatedRewardsExisting) {
      updatedRewards = await Promise.all(
        updatedRewardItems.map(async ({ id, chance, ...rewardParams }) => {
          const { type, value } = await updateReward({
            rewardId: id,
            payload: rewardParams,
          }).unwrap();

          return new RewardModelWithChance(id, type, value, chance);
        })
      );
    }
    const { referralValidationRewards } = referralRewardSettings!;

    await updateReferralRewardSettings({
      ...settingsData,
      referralValidationRewards,
      rewardsChances: transformRewardsFromMapToObject([...createdRewards, ...updatedRewards, ...restRewardItems]),
    }).unwrap();
  };

  const handleOpenMarkReferralRewardModal = () => {
    setMarkReferralRewardModalOpened(true);
  };

  const handleConfirmModalClose = () => {
    setMarkReferralRewardModalOpened(false);
  };

  const handleMarkReferralRewardAsReceivedClick = async () => {
    await markReferralRewardAsReceived(currentUserId).unwrap();

    toast.success('Referral reward has been marked as received');

    await refreshReferralsTable();

    handleConfirmModalClose();
    handleUserProgressEditModalClose();
  };

  const initialValues =
    isNonNullish(referralRewardSettings) && !isEmpty(referralRewardSettings)
      ? getReferralSystemEditFormInitialValues(referralRewardSettings!, initialRewards)
      : REFERRAL_SETTINGS_DEFAULT_VALUES;

  const handleUserProgressEditModalOpen = async (userId: string) => {
    const userProgress = await getReferralUserRewardProgress(userId).unwrap();

    if (isEmpty(userProgress)) {
      toast.error('User progress not found');

      return;
    }

    setCurrentUserId(userId);
    setCurrentUserRewardChance(userProgress.personalChance ?? referralRewardSettings!.rewardsChances);
    setCurrentUserProgress(userProgress);

    setIsUserChanceEditModalOpened(true);
  };

  const handleAssignUserModalOpen = (userId: string) => {
    setCurrentUserId(userId);

    setIsAssignUserModalOpened(true);
  };

  const handleAssignUserModalOpenClose = () => {
    setCurrentUserId('');

    setIsAssignUserModalOpened(false);
  };

  const handleUserProgressEditModalClose = () => {
    setCurrentUserId('');

    setIsUserChanceEditModalOpened(false);
  };

  return (
    <>
      <PageLayout>
        <BaseButton extraClass={exportButton} onClick={handleExportUsersProgress}>
          Export users progress
        </BaseButton>
        <SettingsLayout
          formCaption="Update Settings"
          itemsListComponent={
            <ReferralUsersList
              serverDataGridRef={serverDataGridRef}
              refreshTable={refreshReferralsTable}
              handleUserProgressEditModalOpen={handleUserProgressEditModalOpen}
              handleAssignUserModalOpen={handleAssignUserModalOpen}
              referralSystemStartDate={referralRewardSettings?.startDate}
            />
          }
          formComponent={
            <ReferralRewardSettingsForm
              isSubmitting={isReferralsSettingsUpdating}
              onSubmit={handleUpdatingReferralsSettings}
              initialValues={initialValues}
            />
          }
        />
      </PageLayout>
      <ReferralRewardUserUpdateModal
        isOpened={isUserChanceEditModalOpened}
        handleClose={handleUserProgressEditModalClose}
        referralRewardValue={initialValues.rewardValue}
        userId={currentUserId}
        personalChance={currentUserRewardChance}
        userProgress={currentUserProgress}
        handleOpenConfirmModal={handleOpenMarkReferralRewardModal}
        referralSystemStartDate={referralRewardSettings?.startDate}
      />
      <ConfirmationModal
        buttonConfirmedText="Mark"
        headerTitle="Mark referral reward as claimed"
        onSubmit={handleMarkReferralRewardAsReceivedClick}
        isOpened={markReferralRewardModalOpened}
        handleClose={handleConfirmModalClose}
      >
        Are you sure that you want to mark reward as claimed for this user?
      </ConfirmationModal>
      <ReferralAssignModal
        isOpened={isAssignUserModalOpened}
        handleClose={handleAssignUserModalOpenClose}
        userId={currentUserId}
      />
    </>
  );
};
